import React from "react";
import { connect } from "react-redux";
import { Badge, Button, Card, Col, Dropdown, Form, Nav, Row, Tab } from "react-bootstrap";
import PropTypes from "prop-types";
import Select from "react-select";
import { DndContext, PointerSensor, useDraggable, useDroppable, useSensor, useSensors } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";

import CsLineIcons from "../../cs-line-icons";
import { send as pageInfo, loading } from "../../actions/page";
import { workQueue as getLeadQueue, list as getLeadList, analytics as getLeadAnalytics, completeActivity, snoozeActivity, updateStage, assign as assignLeads } from "../../actions/leads";
import { get as getStaff } from "../../actions/staff";
import { get as getSetting } from "../../actions/settings";
import { format as formatDate } from "../../utils/date";
import { withNavigation } from "../../utils/wrapper";
import Table from "../../layout/table";
import { initialState } from "../../layout/table/helpers";
import NavItem from "../../layout/tabs/item";

const emptyQueue = () => ({
  overdue: [],
  today: [],
  upcoming: [],
  unassigned: [],
  statusCounts: {},
  stageCounts: {},
});

const personLabel = (item) => item.personName || item.personEmail || item.leadSubject || "Lead";
const statusOptions = [
  { value: "OPEN", label: "Open" },
  { value: "HIRED", label: "Hired" },
  { value: "NOT_HIRED", label: "Not Hired" },
  { value: "ARCHIVED", label: "Archived" },
  { value: "ALL", label: "All" },
];

const statusLabels = {
  OPEN: "Open",
  HIRED: "Hired",
  NOT_HIRED: "Not Hired",
  ARCHIVED: "Archived",
};

const statusBadgeVariant = (status) => ({
  OPEN: "primary",
  HIRED: "success",
  NOT_HIRED: "danger",
  ARCHIVED: "secondary",
}[String(status || "OPEN").toUpperCase()] || "secondary");

const safeParse = (value, fallback = []) => {
  try {
    const parsed = typeof value === "string" ? JSON.parse(value) : value;
    return Array.isArray(parsed) ? parsed : fallback;
  } catch (_err) {
    return fallback;
  }
};

const normalizeStageLabel = (item, index) => {
  if (item && typeof item === "object") return item.label || item.value || `Stage ${index}`;
  return item || `Stage ${index}`;
};

const statusFromStageLabel = (label) => {
  const normalized = String(label || "").trim().toLowerCase().replace(/[\s_-]+/g, "");
  if (normalized === "hired") return "HIRED";
  if (normalized === "nothired") return "NOT_HIRED";
  if (normalized === "archived") return "ARCHIVED";
  return null;
};

const missingInfoLabel = (item) => {
  const missing = Array.isArray(item?.missingInfo) ? item.missingInfo : [];
  if (!missing.length) return "";
  return missing.length > 2 ? `${missing.slice(0, 2).join(", ")} +${missing.length - 2}` : missing.join(", ");
};

const ActionToggle = React.forwardRef(({ onClick }, ref) => (
  <Button
    ref={ref}
    variant="outline-primary"
    className="btn-sm btn-icon btn-icon-only shadow-none"
    aria-label="Lead follow-up actions"
    onClick={(event) => {
      event.preventDefault();
      onClick(event);
    }}
  >
    <CsLineIcons icon="more-horizontal" />
  </Button>
));

const DraggableLeadCard = ({ item, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `lead:${item.uuid}`,
    data: {
      item,
    },
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.65 : 1,
        zIndex: isDragging ? 3 : "auto",
        position: "relative",
      }}
    >
      {children({ attributes, listeners, isDragging })}
    </div>
  );
};

const DroppableStageColumn = ({ column, children }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: `stage:${column.stage}`,
    data: {
      stage: column.stage,
      status: column.status || null,
    },
  });

  return (
    <Card className={`h-100 ${isOver ? "border-primary" : ""}`}>
      <Card.Body ref={setNodeRef} style={{ minHeight: 180 }}>
        {children}
      </Card.Body>
    </Card>
  );
};

const LeadBoardDndContext = ({ onDragEnd, children }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 5,
      },
    })
  );

  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd}>
      {children}
    </DndContext>
  );
};

class Leads extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      activeTab: "follow-ups",
      queue: emptyQueue(),
      board: {
        items: [],
        stages: [],
      },
      analytics: {
        total: 0,
        open: 0,
        hired: 0,
        notHired: 0,
        archived: 0,
        overdue: 0,
        noFollowUp: 0,
        missingInfo: 0,
        byStatus: {},
        byStage: {},
        bySource: {},
        byAssignee: {},
      },
      staff: [],
      selectedLeadUuids: [],
      assignmentStaffUuid: "",
      list: initialState({ size: 25, order: "updatedAt", direction: "DESC" }),
      filters: {
        status: "OPEN",
        unassigned: false,
        overdue: false,
      },
    };
    this.props.loading(true);
  }

  componentDidMount = () => {
    setTimeout(this.loadData, 30);
  };

  loadData = async () => {
    await Promise.all([this.loadQueue(), this.loadList(), this.loadBoard(), this.loadAnalytics(), this.loadStaff()]);
    await this.props.pageInfo({ title: "Leads", breadcrumb: "Leads" });
  };

  loadAnalytics = async () => {
    const analytics = await this.props.getLeadAnalytics();
    if (analytics?.success) this.setState({ analytics });
  };

  loadStaff = async () => {
    const staff = await this.props.getStaff({});
    this.setState({ staff: Array.isArray(staff) ? staff : [] });
  };

  loadQueue = async () => {
    this.props.loading(true);
    const queue = await this.props.getLeadQueue({ mine: true });
    this.setState({ queue: queue?.success ? queue : emptyQueue() });
    this.props.loading(false);
  };

  loadList = async () => {
    this.props.loading(true);
    const { list, filters } = this.state;
    const result = await this.props.getLeadList({
      page: list.page,
      size: list.size,
      order: list.order,
      direction: list.direction,
      search: list.search,
      ...filters,
    });
    const count = Number(result?.count || 0);
    const pages = list.size > 0 ? Math.ceil(count / Number(list.size)) : 0;
    this.setState({
      list: {
        ...list,
        data: result?.items || [],
        pages,
      },
      selectedLeadUuids: this.state.selectedLeadUuids.filter((uuid) => (result?.items || []).some((item) => item.uuid === uuid)),
    });
    this.props.loading(false);
  };

  loadBoard = async () => {
    this.props.loading(true);

    const [result, stageSetting] = await Promise.all([
      this.props.getLeadList({
        page: 0,
        size: 100,
        order: "stage",
        direction: "ASC",
        search: "",
        status: "ALL",
      }),
      this.props.getSetting({ name: "LEAD_STAGE", category: "Collections" }),
    ]);

    this.setState({
      board: {
        items: result?.items || [],
        stages: safeParse(stageSetting?.value, []),
      },
    });

    this.props.loading(false);
  };

  onOpenLead = (item) => {
    const leadUuid = item?.leadUuid || item?.uuid;
    if (leadUuid) this.props.navigate(`/dashboard/people/${leadUuid}`);
  };

  onComplete = async (item) => {
    if (!item?.uuid) return;
    await this.props.completeActivity({ uuid: item.uuid });
    await this.loadData();
  };

  onSnooze = async (item, days) => {
    if (!item?.uuid) return;
    await this.props.snoozeActivity({ uuid: item.uuid, days });
    await this.loadData();
  };

  onMoveStage = async (item, stage, status = "OPEN") => {
    if (!item?.uuid) return;

    const result = await this.props.updateStage({ uuid: item.uuid, stage, status });
    if (result?.success) await this.loadData();
  };

  onBoardDragEnd = async ({ active, over }) => {
    if (!active?.data?.current?.item || !over?.data?.current) return;

    const item = active.data.current.item;
    const targetStage = over.data.current.stage;
    const targetStatus = over.data.current.status || "OPEN";
    const validTarget = this.boardColumns.some((column) => Number(column.stage) === Number(targetStage));
    const isSameStage = Number(item.stage ?? 0) === Number(targetStage);
    const isSameStatus = String(item.status || "OPEN").toUpperCase() === targetStatus;

    if (!validTarget || (isSameStage && isSameStatus)) return;
    await this.onMoveStage(item, targetStage, targetStatus);
  };

  onSelectLead = (event, uuid) => {
    const checked = event.target.checked;
    this.setState((prev) => ({
      selectedLeadUuids: checked
        ? [...new Set([...prev.selectedLeadUuids, uuid])]
        : prev.selectedLeadUuids.filter((item) => item !== uuid),
    }));
  };

  onSelectVisibleLeads = (event) => {
    const checked = event.target.checked;
    const visibleUuids = (this.state.list.data || []).map((item) => item.uuid);
    this.setState((prev) => ({
      selectedLeadUuids: checked
        ? [...new Set([...prev.selectedLeadUuids, ...visibleUuids])]
        : prev.selectedLeadUuids.filter((uuid) => !visibleUuids.includes(uuid)),
    }));
  };

  onAssignmentStaffChange = (option) => {
    this.setState({ assignmentStaffUuid: option?.value || "" });
  };

  onAssignSelected = async () => {
    const { selectedLeadUuids, assignmentStaffUuid } = this.state;
    if (!selectedLeadUuids.length) return;

    const result = await this.props.assignLeads({
      leadUuids: selectedLeadUuids,
      assignedToUuid: assignmentStaffUuid || null,
    });

    if (result?.success) {
      this.setState({ selectedLeadUuids: [], assignmentStaffUuid: "" });
      await this.loadData();
    }
  };

  onTabSelect = (e, activeTab) => {
    e?.preventDefault?.();
    this.setState({ activeTab });
  };

  onListSearch = (search) => {
    this.setState((prev) => ({
      list: {
        ...prev.list,
        search,
        page: 0,
      },
    }), this.loadList);
  };

  onListSize = (size) => {
    this.setState((prev) => ({
      list: {
        ...prev.list,
        size: Number(size),
        page: 0,
      },
    }), this.loadList);
  };

  onListPage = (page) => {
    this.setState((prev) => ({
      list: {
        ...prev.list,
        page: Number(page),
      },
    }), this.loadList);
  };

  onListColumnClick = (header) => {
    if (!header?.sortable) return;

    this.setState((prev) => ({
      list: {
        ...prev.list,
        order: header.source,
        direction: prev.list.order === header.source && prev.list.direction === "ASC" ? "DESC" : "ASC",
        page: 0,
      },
    }), this.loadList);
  };

  onStatusChange = (option) => {
    this.setState((prev) => ({
      filters: {
        ...prev.filters,
        status: option?.value || "OPEN",
      },
      list: {
        ...prev.list,
        page: 0,
      },
    }), this.loadList);
  };

  onFilterCheck = (event) => {
    const { name, checked } = event.target;
    this.setState((prev) => ({
      filters: {
        ...prev.filters,
        [name]: checked,
      },
      list: {
        ...prev.list,
        page: 0,
      },
    }), this.loadList);
  };

  get listHeaders() {
    return [
      {
        header: "",
        source: "selected",
        className: "text-muted text-small text-uppercase",
        onRender: (_value, row) => (
          <Form.Check
            type="checkbox"
            checked={this.state.selectedLeadUuids.includes(row.uuid)}
            onChange={(event) => this.onSelectLead(event, row.uuid)}
          />
        ),
      },
      {
        header: "Name",
        sortable: true,
        source: "personName",
        className: "text-muted text-small text-uppercase w-25",
        onFormat: (value, row) => `<a href="/dashboard/people/${row.uuid}">${value || row.personEmail || row.uuid}</a>`,
      },
      {
        header: "Subject",
        sortable: true,
        source: "subject",
        className: "text-muted text-small text-uppercase w-25",
      },
      {
        header: "Status",
        sortable: true,
        source: "status",
        className: "text-muted text-small text-uppercase w-10",
        onRender: (value) => {
          const status = String(value || "OPEN").toUpperCase();
          return <Badge bg={statusBadgeVariant(status)}>{statusLabels[status] || status}</Badge>;
        },
      },
      {
        header: "Assigned",
        sortable: true,
        source: "assignedToName",
        className: "text-muted text-small text-uppercase w-15",
      },
      {
        header: "Missing Info",
        sortable: false,
        source: "missingInfoCount",
        className: "text-muted text-small text-uppercase w-15",
        onRender: (_value, row) => row.missingInfoCount ? (
          <Badge bg="warning" text="dark" title={(row.missingInfo || []).join(", ")}>
            {missingInfoLabel(row)}
          </Badge>
        ) : (
          <Badge bg="light" text="dark">Complete</Badge>
        ),
      },
      {
        header: "Next Follow-Up",
        sortable: true,
        source: "nextFollowUpAt",
        className: "text-muted text-small text-uppercase w-15",
        onFormat: (value) => value ? formatDate(value, true) : "",
      },
      {
        header: "Updated",
        sortable: true,
        source: "updatedAt",
        className: "text-muted text-small text-uppercase w-10",
        onFormat: (value) => value ? formatDate(value, true) : "",
      },
    ];
  }

  renderSummary = () => {
    const { queue } = this.state;
    const stages = Object.entries(queue.stageCounts || {});

    return (
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md="3" className="mb-2">
              <div className="text-muted text-small text-uppercase">Open Leads</div>
              <div className="h4 mb-0">{queue.statusCounts?.OPEN || 0}</div>
            </Col>
            <Col md="3" className="mb-2">
              <div className="text-muted text-small text-uppercase">Overdue</div>
              <div className="h4 mb-0 text-danger">{queue.overdue?.length || 0}</div>
            </Col>
            <Col md="3" className="mb-2">
              <div className="text-muted text-small text-uppercase">Due Today</div>
              <div className="h4 mb-0">{queue.today?.length || 0}</div>
            </Col>
            <Col md="3" className="mb-2">
              <div className="text-muted text-small text-uppercase">Unassigned</div>
              <div className="h4 mb-0">{queue.unassigned?.length || 0}</div>
            </Col>
          </Row>
          {stages.length ? (
            <div className="mt-3">
              {stages.map(([stage, count]) => (
                <Badge key={stage} bg="light" text="primary" className="me-2 border">
                  Stage {stage}: {count}
                </Badge>
              ))}
            </div>
          ) : null}
        </Card.Body>
      </Card>
    );
  };

  renderActivityItem = (item, variant) => (
    <div key={item.uuid} className="border-bottom py-3">
      <Row className="align-items-center">
        <Col md="7">
          <div>
            <Button variant="link" className="p-0 text-start" onClick={() => this.onOpenLead(item)}>
              {personLabel(item)}
            </Button>
            {item.dueAt ? (
              <Badge bg={variant} className="ms-2">
                Due {formatDate(item.dueAt, true)}
              </Badge>
            ) : null}
          </div>
          <div className="text-muted">
            {item.subject || item.body || "Follow up with lead"}
          </div>
          {item.assignedToName ? <div className="text-small text-muted">{item.assignedToName}</div> : null}
        </Col>
        <Col md="5" className="text-md-end mt-2 mt-md-0">
          <Dropdown align="end">
            <Dropdown.Toggle as={ActionToggle} />
            <Dropdown.Menu>
              <Dropdown.Item href="#" onClick={(event) => { event.preventDefault(); this.onOpenLead(item); }}>
                Open
              </Dropdown.Item>
              <Dropdown.Item href="#" onClick={(event) => { event.preventDefault(); this.onSnooze(item, 1); }}>
                Snooze 1 day
              </Dropdown.Item>
              <Dropdown.Item href="#" onClick={(event) => { event.preventDefault(); this.onSnooze(item, 3); }}>
                Snooze 3 days
              </Dropdown.Item>
              <Dropdown.Item href="#" onClick={(event) => { event.preventDefault(); this.onSnooze(item, 7); }}>
                Snooze 7 days
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item href="#" onClick={(event) => { event.preventDefault(); this.onComplete(item); }}>
                Complete
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </Col>
      </Row>
    </div>
  );

  renderLeadItem = (item) => (
    <div key={item.leadUuid} className="border-bottom py-3">
      <Row className="align-items-center">
        <Col md="8">
          <Button variant="link" className="p-0 text-start" onClick={() => this.onOpenLead(item)}>
            {personLabel(item)}
          </Button>
          <div className="text-muted">{item.leadSubject || item.personEmail || "Open lead"}</div>
        </Col>
        <Col md="4" className="text-md-end mt-2 mt-md-0">
          <Button variant="outline-primary" size="sm" onClick={() => this.onOpenLead(item)}>
            Open
          </Button>
        </Col>
      </Row>
    </div>
  );

  renderSection = ({ title, items, variant = "secondary", empty, renderItem }) => (
    <Card className="mb-4">
      <Card.Body>
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h2 className="small-title mb-0">{title}</h2>
          <Badge bg="light" text="dark">{items.length}</Badge>
        </div>
        {items.length ? items.map((item) => renderItem(item, variant)) : <div className="text-muted py-3">{empty}</div>}
      </Card.Body>
    </Card>
  );

  renderQueue = () => {
    const { queue } = this.state;

    return (
      <>
        {this.renderSummary()}
        <Row>
          <Col lg="6">
            {this.renderSection({
              title: "Overdue Follow-Ups",
              items: queue.overdue || [],
              variant: "danger",
              empty: "No overdue lead follow-ups.",
              renderItem: this.renderActivityItem,
            })}
            {this.renderSection({
              title: "Due Today",
              items: queue.today || [],
              variant: "primary",
              empty: "No lead follow-ups due today.",
              renderItem: this.renderActivityItem,
            })}
          </Col>
          <Col lg="6">
            {this.renderSection({
              title: "Upcoming",
              items: queue.upcoming || [],
              variant: "secondary",
              empty: "No upcoming lead follow-ups.",
              renderItem: this.renderActivityItem,
            })}
            {this.renderSection({
              title: "Unassigned Leads",
              items: queue.unassigned || [],
              empty: "No unassigned open leads.",
              renderItem: this.renderLeadItem,
            })}
          </Col>
        </Row>
      </>
    );
  };

  renderListFilter = () => (
    <Row className="mb-3 g-3">
      <Col md="4" lg="3">
        <Form.Label>Status</Form.Label>
        <Select
          classNamePrefix="react-select"
          options={statusOptions}
          value={statusOptions.find((item) => item.value === this.state.filters.status) || statusOptions[0]}
          onChange={this.onStatusChange}
        />
      </Col>
      <Col md="8" lg="3" className="d-flex align-items-end">
        <Form.Check
          className="me-4 mb-2"
          type="checkbox"
          name="unassigned"
          label="Unassigned"
          checked={this.state.filters.unassigned}
          onChange={this.onFilterCheck}
        />
        <Form.Check
          className="mb-2"
          type="checkbox"
          name="overdue"
          label="Overdue follow-up"
          checked={this.state.filters.overdue}
          onChange={this.onFilterCheck}
        />
      </Col>
      <Col lg="6">
        <Form.Label>Assign Selected</Form.Label>
        <div className="d-flex gap-2">
          <Select
            className="flex-grow-1"
            classNamePrefix="react-select"
            options={this.staffOptions}
            value={this.staffOptions.find((item) => item.value === this.state.assignmentStaffUuid) || null}
            onChange={this.onAssignmentStaffChange}
            isClearable
          />
          <Button
            variant="outline-primary"
            disabled={!this.state.selectedLeadUuids.length}
            onClick={this.onAssignSelected}
          >
            Assign {this.state.selectedLeadUuids.length || ""}
          </Button>
        </div>
      </Col>
    </Row>
  );

  get staffOptions() {
    return (this.state.staff || []).map((staff) => ({
      value: staff.uuid,
      label: `${staff.Person?.displayName || [staff.Person?.firstName, staff.Person?.lastName].filter(Boolean).join(" ") || staff.uuid}${staff.title ? ` (${staff.title})` : ""}`,
    }));
  }

  renderList = () => {
    const { list } = this.state;

    return (
      <Card>
        <Card.Body>
          <Table
            headers={this.listHeaders}
            page={list.data}
            sort={{ index: list.index, direction: list.direction }}
            size={list.size}
            index={list.page}
            pages={list.pages}
            onColumnClick={this.onListColumnClick}
            onPageChange={this.onListPage}
            onSearch={this.onListSearch}
            onSize={this.onListSize}
            filter={(
              <>
                {this.renderListFilter()}
                <Form.Check
                  type="checkbox"
                  label="Select visible leads"
                  checked={(list.data || []).length > 0 && (list.data || []).every((item) => this.state.selectedLeadUuids.includes(item.uuid))}
                  onChange={this.onSelectVisibleLeads}
                  className="mb-3"
                />
              </>
            )}
            searchable
            sizable
          />
        </Card.Body>
      </Card>
    );
  };

  get boardColumns() {
    const items = this.state.board.items || [];
    const stageValues = new Set((this.state.board.stages || []).map((_item, index) => String(index)));
    items.forEach((item) => stageValues.add(String(item.stage ?? 0)));

    return [...stageValues]
      .map((value) => Number(value))
      .filter((value) => !Number.isNaN(value))
      .sort((a, b) => a - b)
      .map((stage) => {
        const label = normalizeStageLabel(this.state.board.stages?.[stage], stage);
        const status = statusFromStageLabel(label);

        return {
          stage,
          label,
          status,
          items: items.filter((item) => {
            const itemStatus = String(item.status || "OPEN").toUpperCase();
            if (status) return itemStatus === status;
            return itemStatus === "OPEN" && Number(item.stage ?? 0) === stage;
          }),
        };
      });
  }

  renderLeadCard = (item) => {
    const dueAt = item.nextFollowUpAt ? new Date(item.nextFollowUpAt) : null;
    const overdue = dueAt && dueAt < new Date(new Date().setHours(0, 0, 0, 0));

    return (
      <DraggableLeadCard key={item.uuid} item={item}>
        {({ attributes, listeners, isDragging }) => (
          <button
            type="button"
            className={`w-100 text-start border rounded bg-white p-3 mb-2 ${isDragging ? "shadow" : ""}`}
            onDoubleClick={() => this.onOpenLead(item)}
            {...attributes}
            {...listeners}
          >
            <div className="d-flex justify-content-between align-items-start gap-2">
              <div className="fw-bold">{item.personName || item.personEmail || "Lead"}</div>
            </div>
            {item.subject ? <div className="text-muted small mt-1">{item.subject}</div> : null}
            <div className="mt-2">
              {item.missingInfoCount ? (
                <Badge bg="warning" text="dark" title={(item.missingInfo || []).join(", ")} className="me-2">
                  Missing {item.missingInfoCount}
                </Badge>
              ) : null}
              {overdue ? <Badge bg="danger" className="me-2">Overdue</Badge> : null}
              {item.nextFollowUpAt ? (
                <Badge bg={overdue ? "danger" : "light"} text={overdue ? undefined : "dark"} className="me-2">
                  Follow-Up {formatDate(item.nextFollowUpAt)}
                </Badge>
              ) : (
                <Badge bg="warning" text="dark" className="me-2">No Follow-Up</Badge>
              )}
              {item.assignedToName ? <Badge bg="light" text="dark">{item.assignedToName}</Badge> : <Badge bg="secondary">Unassigned</Badge>}
            </div>
          </button>
        )}
      </DraggableLeadCard>
    );
  };

  renderBoard = () => {
    const columns = this.boardColumns;

    return (
      <LeadBoardDndContext onDragEnd={this.onBoardDragEnd}>
        <Row className="g-3 flex-nowrap overflow-auto pb-3">
          {columns.length ? columns.map((column) => (
            <Col key={column.stage} style={{ minWidth: 300, maxWidth: 360 }}>
              <DroppableStageColumn column={column}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h2 className="small-title mb-0">{column.label}</h2>
                  <Badge bg="light" text="dark">{column.items.length}</Badge>
                </div>
                {column.items.length ? column.items.map((item) => this.renderLeadCard(item)) : (
                  <div className="text-muted small py-3">No open leads.</div>
                )}
              </DroppableStageColumn>
            </Col>
          )) : (
            <Col xs="12">
              <Card>
                <Card.Body className="text-muted">No open leads.</Card.Body>
              </Card>
            </Col>
          )}
        </Row>
      </LeadBoardDndContext>
    );
  };

  renderMetric = (caption, value, variant = "dark") => (
    <Col md="3" className="mb-3">
      <Card className="h-100">
        <Card.Body>
          <div className="text-muted text-small text-uppercase">{caption}</div>
          <div className={`h4 mb-0 text-${variant}`}>{value || 0}</div>
        </Card.Body>
      </Card>
    </Col>
  );

  renderBreakdown = (title, items) => {
    const rows = Object.entries(items || {})
      .sort((a, b) => Number(b[1]) - Number(a[1]))
      .slice(0, 12);

    return (
      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <h2 className="small-title mb-0">{title}</h2>
            <Badge bg="light" text="dark">{rows.length}</Badge>
          </div>
          {rows.length ? rows.map(([label, count]) => (
            <div key={`${title}.${label}`} className="d-flex justify-content-between border-bottom py-2">
              <span>{label}</span>
              <Badge bg="light" text="dark">{count}</Badge>
            </div>
          )) : <div className="text-muted py-3">No data.</div>}
        </Card.Body>
      </Card>
    );
  };

  renderAnalytics = () => {
    const { analytics } = this.state;

    return (
      <>
        <Row>
          {this.renderMetric("Total Leads", analytics.total)}
          {this.renderMetric("Open", analytics.open, "primary")}
          {this.renderMetric("Hired", analytics.hired, "success")}
          {this.renderMetric("Not Hired", analytics.notHired, "danger")}
          {this.renderMetric("Overdue", analytics.overdue, "danger")}
          {this.renderMetric("No Follow-Up", analytics.noFollowUp, "warning")}
          {this.renderMetric("Missing Info", analytics.missingInfo, "warning")}
          {this.renderMetric("Archived", analytics.archived, "secondary")}
        </Row>
        <Row>
          <Col lg="6">{this.renderBreakdown("By Source", analytics.bySource)}</Col>
          <Col lg="6">{this.renderBreakdown("By Assignee", analytics.byAssignee)}</Col>
          <Col lg="6">{this.renderBreakdown("By Stage", analytics.byStage)}</Col>
          <Col lg="6">{this.renderBreakdown("By Status", analytics.byStatus)}</Col>
        </Row>
      </>
    );
  };

  render() {
    return (
      <Col xs="12">
        <Tab.Container activeKey={this.state.activeTab}>
          <Card.Header className="border-0 pb-0" style={{ backgroundColor: "white" }}>
            <Nav className="nav-tabs-line card-header-tabs" variant="tabs" activeKey={this.state.activeTab} style={{ backgroundColor: "white" }}>
              <NavItem
                name="follow-ups"
                caption="Follow-Ups"
                selected={this.state.activeTab}
                created
                onClick={(e) => this.onTabSelect(e, "follow-ups")}
              />
              <NavItem
                name="list"
                caption="Lead List"
                selected={this.state.activeTab}
                created
                onClick={(e) => this.onTabSelect(e, "list")}
              />
              <NavItem
                name="board"
                caption="Board"
                selected={this.state.activeTab}
                created
                onClick={(e) => this.onTabSelect(e, "board")}
              />
              <NavItem
                name="analytics"
                caption="Analytics"
                selected={this.state.activeTab}
                created
                onClick={(e) => this.onTabSelect(e, "analytics")}
              />
            </Nav>
          </Card.Header>
          <Tab.Content className="h-100 pt-4">
            <Tab.Pane active={this.state.activeTab === "follow-ups"} style={{
              display: this.state.activeTab === "follow-ups" ? "block" : "none",
            }}>
              {this.renderQueue()}
            </Tab.Pane>
            <Tab.Pane active={this.state.activeTab === "list"} style={{
              display: this.state.activeTab === "list" ? "block" : "none",
            }}>
              {this.renderList()}
            </Tab.Pane>
            <Tab.Pane active={this.state.activeTab === "board"} style={{
              display: this.state.activeTab === "board" ? "block" : "none",
            }}>
              {this.renderBoard()}
            </Tab.Pane>
            <Tab.Pane active={this.state.activeTab === "analytics"} style={{
              display: this.state.activeTab === "analytics" ? "block" : "none",
            }}>
              {this.renderAnalytics()}
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Col>
    );
  }
}

Leads.propTypes = {
  loading: PropTypes.func.isRequired,
  pageInfo: PropTypes.func.isRequired,
  getLeadQueue: PropTypes.func.isRequired,
  getLeadList: PropTypes.func.isRequired,
  getLeadAnalytics: PropTypes.func.isRequired,
  getSetting: PropTypes.func.isRequired,
  completeActivity: PropTypes.func.isRequired,
  snoozeActivity: PropTypes.func.isRequired,
  updateStage: PropTypes.func.isRequired,
  assignLeads: PropTypes.func.isRequired,
  getStaff: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
};

export default withNavigation(
  connect(null, { getLeadQueue, getLeadList, getLeadAnalytics, getSetting, getStaff, completeActivity, snoozeActivity, updateStage, assignLeads, loading, pageInfo })(Leads)
);
